import { breakableHtml, formatDist, safeHref, signalTargetLabel } from '../utils/formatters';

var LOCAL_VOTES_STORAGE_KEY = 'rcn-community-signal-votes';
// How many approved photos the collapsed card previews before it falls back to a "+N" badge.
var SUMMARY_PREVIEW_LIMIT = 3;

function getEntityKey(entityType, entityId) {
    if (!entityType || entityId == null || entityId === '') {
        return null;
    }

    return String(entityType) + ':' + String(entityId);
}

function normalizeVoteValue(value) {
    var parsed = parseInt(value, 10);

    if (parsed === 1 || parsed === -1) {
        return parsed;
    }

    return 0;
}

function readStoredVotes() {
    try {
        var raw = window.localStorage.getItem(LOCAL_VOTES_STORAGE_KEY);
        if (!raw) {
            return {};
        }

        var parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (_error) {
        return {};
    }
}

function writeStoredVotes(votesBySignalId) {
    try {
        window.localStorage.setItem(LOCAL_VOTES_STORAGE_KEY, JSON.stringify(votesBySignalId || {}));
    } catch (_error) {
        // Ignore storage errors.
    }
}

export function createSignalsPanel(config) {
    var entityCache = {};
    var activeRequest = null;
    var pendingFocus = null;
    var votesBySignalId = readStoredVotes();
    var lightbox = null;
    var currentEntityKey = null;

    function ensureLightbox() {
        if (lightbox) {
            return lightbox;
        }

        var overlay = document.createElement('div');
        overlay.className = 'nm-signal-lightbox';
        overlay.hidden = true;
        overlay.innerHTML = ''
            + '<div class="nm-signal-lightbox__backdrop" data-signal-lightbox-close></div>'
            + '<div class="nm-signal-lightbox__dialog" role="dialog" aria-modal="true" aria-label="Podgląd zdjęcia sygnału">'
            + '  <button class="ms-button ms-button--icon-lg ms-button--overlay-icon nm-signal-lightbox__close" type="button" data-signal-lightbox-close aria-label="Zamknij"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg></button>'
            + '  <button class="ms-button ms-button--icon-lg ms-button--overlay-icon nm-signal-lightbox__nav nm-signal-lightbox__nav--prev" type="button" data-signal-lightbox-prev aria-label="Poprzednie zdjęcie"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 6l-6 6 6 6"/></svg></button>'
            + '  <img class="nm-signal-lightbox__image" alt="Załącznik sygnału społeczności">'
            + '  <button class="ms-button ms-button--icon-lg ms-button--overlay-icon nm-signal-lightbox__nav nm-signal-lightbox__nav--next" type="button" data-signal-lightbox-next aria-label="Następne zdjęcie"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg></button>'
            + '  <div class="nm-signal-lightbox__meta"><span data-signal-lightbox-counter></span></div>'
            + '</div>';

        document.body.appendChild(overlay);

        lightbox = {
            overlay: overlay,
            imageEl: overlay.querySelector('.nm-signal-lightbox__image'),
            counterEl: overlay.querySelector('[data-signal-lightbox-counter]'),
            attachments: [],
            index: -1,
        };

        overlay.addEventListener('click', function(event) {
            if (event.target.closest('[data-signal-lightbox-close]')) {
                closeLightbox();
                return;
            }

            if (event.target.closest('[data-signal-lightbox-prev]')) {
                moveLightbox(-1);
                return;
            }

            if (event.target.closest('[data-signal-lightbox-next]')) {
                moveLightbox(1);
            }
        });

        document.addEventListener('keydown', function(event) {
            if (!lightbox || lightbox.overlay.hidden) {
                return;
            }

            if (event.key === 'Escape') {
                closeLightbox();
                return;
            }

            if (event.key === 'ArrowLeft') {
                moveLightbox(-1);
                return;
            }

            if (event.key === 'ArrowRight') {
                moveLightbox(1);
            }
        });

        return lightbox;
    }

    function openLightbox(attachments, index) {
        if (!Array.isArray(attachments) || !attachments.length) {
            return;
        }

        ensureLightbox();
        lightbox.attachments = attachments;
        lightbox.index = Math.max(0, Math.min(index || 0, attachments.length - 1));
        lightbox.overlay.hidden = false;
        document.body.classList.add('nm-signal-lightbox-open');
        syncLightbox();
    }

    function closeLightbox() {
        if (!lightbox) {
            return;
        }

        lightbox.overlay.hidden = true;
        lightbox.attachments = [];
        lightbox.index = -1;
        document.body.classList.remove('nm-signal-lightbox-open');
    }

    function moveLightbox(direction) {
        if (!lightbox || lightbox.overlay.hidden || !lightbox.attachments.length) {
            return;
        }

        var nextIndex = lightbox.index + direction;
        if (nextIndex < 0) {
            nextIndex = lightbox.attachments.length - 1;
        } else if (nextIndex >= lightbox.attachments.length) {
            nextIndex = 0;
        }

        lightbox.index = nextIndex;
        syncLightbox();
    }

    function syncLightbox() {
        if (!lightbox || lightbox.overlay.hidden || !lightbox.attachments.length) {
            return;
        }

        var attachment = lightbox.attachments[lightbox.index];
        if (!attachment) {
            return;
        }

        lightbox.imageEl.src = attachment.url || '';
        lightbox.counterEl.textContent = (lightbox.index + 1) + ' / ' + lightbox.attachments.length;
    }

    function buildSectionShell(context) {
        var entityKey = getEntityKey(context.entityType, context.entityId);

        return ''
            + '<section class="nm-signals-block" data-signal-entity-key="' + config.esc(entityKey || '') + '" data-signal-entity-type="' + config.esc(context.entityType || '') + '" data-signal-entity-id="' + config.esc(String(context.entityId || '')) + '">'
            + '  <div class="nm-signals-block__head">'
            + '    <div class="nm-section-label" style="font-size: 0.85rem; font-weight: 800; color: var(--nm-text); letter-spacing: normal;">Sygnały społeczności'
            + '      <button type="button" class="nm-info-icon" aria-label="' + config.esc(config.disclaimerAriaLabel || 'Informacja o sygnałach') + '" data-signals-info>&#9432;</button>'
            + '    </div>'
            + '    <button class="nm-signals-block__cta" type="button" data-signal-open-submit>' + config.esc(config.submitButtonLabel || 'Dodaj uwagę do miejsca') + '</button>'
            + '  </div>'
            + '  <div class="ms-empty-state nm-loading-state"><strong>Ładowanie…</strong><span>Pobieram opublikowane sygnały dla tego miejsca.</span></div>'
            + '</section>';
    }

    function getStoredVote(signalId) {
        return normalizeVoteValue(votesBySignalId[String(signalId)] || 0);
    }

    function setStoredVote(signalId, value) {
        var normalized = normalizeVoteValue(value);
        if (normalized === 0) {
            delete votesBySignalId[String(signalId)];
        } else {
            votesBySignalId[String(signalId)] = normalized;
        }

        writeStoredVotes(votesBySignalId);
    }

    function getSentimentChip(sentiment) {
        if (sentiment === 'pro') {
            return {
                label: 'Pozytywny',
                className: 'nm-chip--success',
            };
        }

        if (sentiment === 'con') {
            return {
                label: 'Zagrożenie',
                className: 'nm-chip--danger',
            };
        }

        return {
            label: 'Obserwacja',
            className: 'nm-chip--info',
        };
    }

    function formatSignalTimestamp(value) {
        if (!value) {
            return '—';
        }

        var timestamp = new Date(value);
        if (isNaN(timestamp.getTime())) {
            return '—';
        }

        return timestamp.toLocaleString('pl-PL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    function getCoverageTypeLabel(type) {
        if (type === 'linia') {
            return 'Linia';
        }

        if (type === 'obszar') {
            return 'Obszar';
        }

        return 'Punkt';
    }

    function getMapModeLabel(signal) {
        if (signal && signal.highlight_mode === 'radius' && signal.influence_radius_m != null) {
            return 'Promień oddziaływania ' + formatDist(Number(signal.influence_radius_m));
        }

        if (signal && signal.highlight_mode === 'geometry') {
            return getCoverageTypeLabel(signal.coverage_type);
        }

        return 'Punkt';
    }

    function buildSelectionStateHtml(signal, signalCount) {
        if (signal) {
            return ''
                + '<strong>Mapa pokazuje aktywny sygnał</strong>'
                + '<span>' + config.esc((signal.target_label || signal.coverage_label || 'Sygnał społeczności') + ' | ' + getMapModeLabel(signal)) + '</span>';
        }

        if (!signalCount) {
            return '';
        }

        return ''
            + '<strong>Brak aktywnego sygnału</strong>'
            + '<span>Wybierz kartę z listy, aby pokazać na mapie rzeczywisty zasięg sygnału.</span>';
    }

    function syncSelectionState(container, signal) {
        if (!container) {
            return;
        }

        var stateEl = container.querySelector('[data-signal-selection-state]');
        if (!stateEl) {
            return;
        }

        var signalCount = container.querySelectorAll('[data-signal-card]').length;
        stateEl.classList.toggle('is-active', !!signal);
        stateEl.innerHTML = buildSelectionStateHtml(signal, signalCount);
    }

    // `href` (a page of the parcel or building from the API) turns the value into a link.
    // `identifier`: a cadastral identifier, which may wrap between its segments.
    function buildDetailRowHtml(label, value, href, identifier) {
        var safe = value ? safeHref(href) : '';
        var textHtml = identifier && value ? breakableHtml(value) : config.esc(value || '—');
        var valueHtml = safe
            ? '<a class="ms-link" href="' + config.esc(safe) + '">' + textHtml + '</a>'
            : textHtml;
        return ''
            + '<div class="nm-detail-row">'
            + '  <span>' + config.esc(label) + '</span>'
            + '  <span class="nm-detail-value--wrap">' + valueHtml + '</span>'
            + '</div>';
    }

    function buildVoteButtonsHtml(signal, storedVote) {
        var signalIdEsc = config.esc(String(signal.id));
        var labelEsc = config.esc((signal.category && signal.category.name) || ('Sygnał #' + signal.id));
        return ''
            + '<div class="nm-signal-votes" data-signal-votes>'
            + '  <button class="ms-button ms-button--secondary ms-button--sm ms-button--pill nm-signal-vote-btn' + (storedVote === 1 ? ' is-active' : '') + '" type="button" data-signal-vote="1" data-signal-id="' + signalIdEsc + '"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"></path></svg> <span data-signal-score-up>' + config.esc(String(signal.score_up || 0)) + '</span></button>'
            + '  <button class="ms-button ms-button--secondary ms-button--sm ms-button--pill nm-signal-vote-btn' + (storedVote === -1 ? ' is-active' : '') + '" type="button" data-signal-vote="-1" data-signal-id="' + signalIdEsc + '"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"></path></svg> <span data-signal-score-down>' + config.esc(String(signal.score_down || 0)) + '</span></button>'
            + '  <button class="ms-button ms-button--secondary ms-button--sm ms-button--pill nm-signal-fav-btn" type="button"'
            + '          data-rcn-fav-toggle'
            + '          data-fav-entity-type="community_signal"'
            + '          data-fav-entity-ref="' + signalIdEsc + '"'
            + '          data-fav-label="' + labelEsc + '"'
            + '          aria-pressed="false"'
            + '          aria-label="Dodaj sygnał do ulubionych" title="Dodaj do ulubionych">'
            + '    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round" aria-hidden="true"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>'
            + '  </button>'
            + '  <button class="ms-button ms-button--secondary ms-button--sm nm-signal-report-btn" type="button" data-signal-report data-signal-id="' + signalIdEsc + '" aria-label="Zgłoś sygnał">&#9888; Zgłoś</button>'
            + '</div>';
    }

    // Only attachments the API actually resolved to a media URL can be shown —
    // an entry without one would render as a broken image placeholder.
    function visibleAttachments(signal) {
        var attachments = Array.isArray(signal && signal.attachments) ? signal.attachments : [];

        return attachments.filter(function(attachment) {
            return attachment && typeof attachment.url === 'string' && attachment.url !== '';
        });
    }

    // The approved photo is the most informative part of a signal, so a preview
    // rides along in the collapsed summary instead of hiding behind "Pokaż szczegóły".
    function buildSummaryPreviewHtml(signal, attachments) {
        if (!attachments.length) {
            return '';
        }

        var signalIdEsc = config.esc(String(signal.id));
        var visible = attachments.slice(0, SUMMARY_PREVIEW_LIMIT);
        var overflow = attachments.length - visible.length;

        // Podgląd otwiera powiększenie tak samo jak miniatury w galerii —
        // zdjęcia nie wymagają wcześniejszego rozwinięcia sygnału.
        return ''
            + '<div class="nm-signal-card__preview">'
            + visible.map(function(attachment, index) {
                return '<button class="nm-signal-card__preview-item" type="button" data-signal-attachment data-signal-id="' + signalIdEsc + '" data-signal-attachment-index="' + index + '" aria-label="Powiększ zdjęcie ' + (index + 1) + ' z ' + attachments.length + '">'
                    + '<img src="' + config.esc(attachment.url) + '" alt="Zdjęcie dołączone do sygnału" loading="lazy" decoding="async">'
                    + '</button>';
            }).join('')
            + (overflow > 0
                ? '<button class="nm-signal-card__preview-more" type="button" data-signal-attachment data-signal-id="' + signalIdEsc + '" data-signal-attachment-index="' + visible.length + '" aria-label="Powiększ pozostałe zdjęcia">+' + overflow + '</button>'
                : '')
            + '</div>';
    }

    function buildGalleryHtml(signal, attachments) {
        if (!attachments.length) {
            return '';
        }

        return ''
            + '<div class="nm-signal-card__gallery">'
            + '  <div class="nm-section-label">Zdjęcia (' + attachments.length + ')</div>'
            + '  <div class="nm-signal-thumbs">'
            + attachments.map(function(attachment, index) {
                return ''
                    + '<button class="nm-signal-thumb" type="button" data-signal-attachment data-signal-id="' + config.esc(String(signal.id)) + '" data-signal-attachment-index="' + index + '" aria-label="Powiększ zdjęcie ' + (index + 1) + ' z ' + attachments.length + '">'
                    + '  <img src="' + config.esc(attachment.url) + '" alt="Zdjęcie dołączone do sygnału" loading="lazy" decoding="async">'
                    + '</button>';
            }).join('')
            + '  </div>'
            + '</div>';
    }

    function buildSignalCardHtml(signal, entityKey) {
        var category = signal.category || {};
        var sentimentMeta = getSentimentChip(category.sentiment);
        var storedVote = getStoredVote(signal.id);
        var attachments = visibleAttachments(signal);
        var summaryTitle = signal.target_label || signal.coverage_label || category.name || 'Sygnał społeczności';
        var summarySubtitleParts = [signal.target_subtitle || signal.coverage_subtitle];
        if (signal.coverage_label && signal.coverage_label !== summaryTitle) {
            summarySubtitleParts.push(signal.coverage_label);
        }
        var summarySubtitle = summarySubtitleParts.filter(Boolean).join(' | ');
        var mapModeLabel = getMapModeLabel(signal);
        var coverageTypeLabel = getCoverageTypeLabel(signal.coverage_type);
        var coverageSourceLabel = signalTargetLabel(signal.coverage_source_type);
        var detailsRows = [
            buildDetailRowHtml('Cel', signal.target_label || signal.coverage_label || 'Punkt na mapie'),
            buildDetailRowHtml('Identyfikator celu', signal.target_identifier, signal.target_detail_url, true),
            buildDetailRowHtml('Zasięg', coverageTypeLabel),
            buildDetailRowHtml('Źródło zasięgu', coverageSourceLabel),
            buildDetailRowHtml('Mapa pokaże', mapModeLabel),
            buildDetailRowHtml('Opis zasięgu', signal.coverage_subtitle || signal.target_subtitle),
            buildDetailRowHtml('Kod podziału terytorialnego', signal.teryt),
            buildDetailRowHtml('Utworzono', formatSignalTimestamp(signal.created_at)),
            buildDetailRowHtml('Zaktualizowano', formatSignalTimestamp(signal.updated_at)),
        ].join('');

        return ''
            + '<article class="nm-signal-card nm-signal-card--' + config.esc(category.sentiment || 'general') + '" data-signal-card data-signal-id="' + config.esc(String(signal.id)) + '" data-signal-entity-key="' + config.esc(entityKey || '') + '">'
            // Podsumowanie nie jest przyciskiem, bo zawiera własne przyciski
            // (podgląd zdjęć i „Pokaż / Zwiń szczegóły”); rozwinięcie obsługuje
            // delegowany handler, zwijanie wyłącznie przycisk pod spodem.
            + '  <div class="nm-signal-card__summary">'
            + '    <div class="nm-signal-card__top">'
            + '      <div class="nm-signal-card__title-wrap">'
            + '        <div class="nm-signal-card__kicker">' + config.esc((category.icon || '💬') + ' ' + (category.name || 'Sygnał społeczności')) + '</div>'
            + '        <div class="nm-signal-card__title">' + config.esc(summaryTitle) + '</div>'
            + (summarySubtitle ? '<div class="nm-signal-card__summary-text">' + config.esc(summarySubtitle) + '</div>' : '')
            + '      </div>'
            + '      <div class="nm-signal-card__indicator">' + config.esc(mapModeLabel) + '</div>'
            + '    </div>'
            + buildSummaryPreviewHtml(signal, attachments)
            + '    <div class="nm-list-meta-row">'
            + '      <span class="nm-chip ' + sentimentMeta.className + '">' + config.esc(sentimentMeta.label) + '</span>'
            + '      <span class="nm-chip"><b>Zasięg</b> ' + config.esc(coverageTypeLabel) + '</span>'
            + '      <span class="nm-chip"><b>Mapa</b> ' + config.esc(mapModeLabel) + '</span>'
            + (attachments.length ? '<span class="nm-chip"><b>Zdjęcia</b> ' + config.esc(String(attachments.length)) + '</span>' : '')
            + '    </div>'
            + '    <button class="nm-signal-card__toggle-label" type="button" data-signal-card-toggle data-signal-toggle-label aria-expanded="false">Pokaż szczegóły</button>'
            + '  </div>'
            + '  <div class="nm-signal-card__details">'
            + '    <div class="nm-signal-card__details-copy">'
            + '      <div class="nm-section-label">Treść sygnału</div>'
            + '      <p class="nm-signal-card__body">' + config.esc(signal.body || '') + '</p>'
            + '    </div>'
            + buildGalleryHtml(signal, attachments)
            + '    <div class="nm-signal-card__details-grid">' + detailsRows + '</div>'
            + '    <div class="nm-signal-card__footer">' + buildVoteButtonsHtml(signal, storedVote) + '</div>'
            + '  </div>'
            + '</article>';
    }

    // Signals are split into sentiment groups so positive and negative
    // entries never blur together in one undifferentiated list.
    function buildSignalListHtml(signals, entityKey) {
        var sections = [
            { key: 'con', icon: '⚠️', label: 'Zagrożenia i uwagi', list: [] },
            { key: 'pro', icon: '👍', label: 'Mocne strony okolicy', list: [] },
            { key: 'general', icon: '💬', label: 'Obserwacje', list: [] },
        ];

        signals.forEach(function(signal) {
            var sentiment = signal.category && signal.category.sentiment;
            var bucket = sentiment === 'pro' ? sections[1] : sentiment === 'con' ? sections[0] : sections[2];
            bucket.list.push(signal);
        });

        var groupsHtml = sections.filter(function(section) {
            return section.list.length;
        }).map(function(section) {
            return '<div class="nm-signal-group nm-signal-group--' + section.key + '">'
                + '<div class="nm-signal-group__head">'
                + '  <span class="nm-signal-group__title">' + section.icon + ' ' + config.esc(section.label) + '</span>'
                + '  <span class="nm-signal-group__count">' + section.list.length + '</span>'
                + '</div>'
                + section.list.map(function(signal) {
                    return buildSignalCardHtml(signal, entityKey);
                }).join('')
                + '</div>';
        }).join('');

        return '<div class="nm-signal-list">' + groupsHtml + '</div>';
    }

    function renderEntitySignals(container, context, data) {
        if (!container) {
            return;
        }

        var signals = Array.isArray(data.signals) ? data.signals : [];
        var entityKey = getEntityKey(context.entityType, context.entityId);
        currentEntityKey = entityKey;

        var bodyHtml = signals.length
            ? buildSignalListHtml(signals, entityKey)
            : '<div class="ms-empty-state nm-empty-state"><strong>Brak publicznych sygnałów</strong><span>Dla tego miejsca nie ma teraz żadnych sygnałów widocznych publicznie.</span></div>';

        container.innerHTML = ''
            + '<div class="nm-signals-block__head">'
            + '  <div class="nm-section-label">Sygnały społeczności'
            + '    <button type="button" class="nm-info-icon" aria-label="' + config.esc(config.disclaimerAriaLabel || 'Informacja o sygnałach') + '" data-signals-info>&#9432;</button>'
            + '  </div>'
            + '  <button class="nm-signals-block__cta" type="button" data-signal-open-submit>' + config.esc(config.submitButtonLabel || 'Dodaj uwagę do miejsca') + '</button>'
            + '</div>'
            + (signals.length ? '<div class="nm-signal-selection-state" data-signal-selection-state>' + buildSelectionStateHtml(null, signals.length) + '</div>' : '')
            + bodyHtml;

        var focusSignalId = context.selectedSignalId != null
            ? String(context.selectedSignalId)
            : (pendingFocus && pendingFocus.entityKey === entityKey ? pendingFocus.signalId : null);

        if (focusSignalId) {
            focusSignal(entityKey, focusSignalId, { suppressScroll: true });
            pendingFocus = null;
        } else if (config.onSignalFocus) {
            syncSelectionState(container, null);
            config.onSignalFocus(null);
        }

        if (
            window.metroskopFavorites
            && typeof window.metroskopFavorites.syncRenderedToggles === 'function'
        ) {
            window.metroskopFavorites.syncRenderedToggles();
        }
    }

    function getSignalFromCache(entityKey, signalId) {
        var cachedData = entityCache[entityKey];
        var signals = cachedData && Array.isArray(cachedData.signals) ? cachedData.signals : [];

        return signals.find(function(signal) {
            return String(signal.id) === String(signalId);
        }) || null;
    }

    function focusSignal(entityKey, signalId, options) {
        options = options || {};

        var container = config.detailsContentEl.querySelector('[data-signal-entity-key="' + entityKey + '"]');
        if (!container) {
            pendingFocus = {
                entityKey: entityKey,
                signalId: String(signalId),
            };
            return;
        }

        var cards = container.querySelectorAll('[data-signal-card]');
        cards.forEach(function(card) {
            var isActive = card.getAttribute('data-signal-id') === String(signalId);
            var toggle = card.querySelector('[data-signal-card-toggle]');
            var toggleLabel = card.querySelector('[data-signal-toggle-label]');
            card.classList.toggle('is-active', isActive);
            if (toggle) {
                toggle.setAttribute('aria-expanded', isActive ? 'true' : 'false');
            }
            if (toggleLabel) {
                toggleLabel.textContent = isActive ? 'Zwiń szczegóły' : 'Pokaż szczegóły';
            }
        });

        if (!options.suppressScroll) {
            var activeCard = container.querySelector('[data-signal-card].is-active');
            if (activeCard && typeof activeCard.scrollIntoView === 'function') {
                activeCard.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
        }

        var signal = getSignalFromCache(entityKey, signalId);
        syncSelectionState(container, signal);
        if (config.onSignalFocus) {
            config.onSignalFocus(signal || null);
        }
        if (signal && config.onSignalSelectionChange) {
            config.onSignalSelectionChange(signal);
        }
    }

    function unfocusSignal(entityKey, signalId) {
        var container = config.detailsContentEl.querySelector('[data-signal-entity-key="' + entityKey + '"]');
        if (!container) {
            return;
        }

        var card = container.querySelector('[data-signal-card][data-signal-id="' + signalId + '"]');
        if (card) {
            card.classList.remove('is-active');
            var toggle = card.querySelector('[data-signal-card-toggle]');
            var toggleLabel = card.querySelector('[data-signal-toggle-label]');
            if (toggle) {
                toggle.setAttribute('aria-expanded', 'false');
            }
            if (toggleLabel) {
                toggleLabel.textContent = 'Pokaż szczegóły';
            }
        }

        syncSelectionState(container, null);
        if (config.onSignalFocus) {
            config.onSignalFocus(null);
        }
    }

    function clearFocus() {
        if (currentEntityKey) {
            syncSelectionState(config.detailsContentEl.querySelector('[data-signal-entity-key="' + currentEntityKey + '"]'), null);
        }

        pendingFocus = null;
        currentEntityKey = null;

        var cards = config.detailsContentEl.querySelectorAll('[data-signal-card].is-active');
        cards.forEach(function(card) {
            card.classList.remove('is-active');
            var toggle = card.querySelector('[data-signal-card-toggle]');
            var toggleLabel = card.querySelector('[data-signal-toggle-label]');
            if (toggle) {
                toggle.setAttribute('aria-expanded', 'false');
            }
            if (toggleLabel) {
                toggleLabel.textContent = 'Pokaż szczegóły';
            }
        });

        if (config.onSignalFocus) {
            config.onSignalFocus(null);
        }
    }

    function setPendingFocus(entityType, entityId, signalId) {
        var entityKey = getEntityKey(entityType, entityId);
        if (!entityKey || signalId == null) {
            pendingFocus = null;
            return;
        }

        pendingFocus = {
            entityKey: entityKey,
            signalId: String(signalId),
        };
    }

    function fetchEntitySignals(context) {
        var entityKey = getEntityKey(context.entityType, context.entityId);
        if (!entityKey) {
            return;
        }

        var container = config.detailsContentEl.querySelector('[data-signal-entity-key="' + entityKey + '"]');
        if (!container) {
            return;
        }

        if (entityCache[entityKey]) {
            renderEntitySignals(container, context, entityCache[entityKey]);
            return;
        }

        if (activeRequest && activeRequest.entityKey === entityKey) {
            if (context.selectedSignalId != null) {
                setPendingFocus(context.entityType, context.entityId, context.selectedSignalId);
            }
            return;
        }

        if (activeRequest && activeRequest.controller) {
            activeRequest.controller.abort();
        }

        var controller = new AbortController();
        activeRequest = {
            entityKey: entityKey,
            controller: controller,
        };

        fetch(
            config.baseUrl + 'api/signals/by-entity?type=' + encodeURIComponent(context.entityType) + '&id=' + encodeURIComponent(String(context.entityId)),
            { signal: controller.signal }
        )
            .then(function(response) {
                if (response.ok) {
                    return response.json();
                }

                return response.json()
                    .catch(function() { return null; })
                    .then(function(payload) {
                        throw new Error(payload && payload.message ? payload.message : 'Nie udało się pobrać sygnałów.');
                    });
            })
            .then(function(payload) {
                if (controller.signal.aborted) {
                    return;
                }

                entityCache[entityKey] = payload || { signals: [] };
                var liveContainer = config.detailsContentEl.querySelector('[data-signal-entity-key="' + entityKey + '"]');
                if (liveContainer) {
                    renderEntitySignals(liveContainer, context, entityCache[entityKey]);
                }
            })
            .catch(function(error) {
                if (error && error.name === 'AbortError') {
                    return;
                }

                var liveContainer = config.detailsContentEl.querySelector('[data-signal-entity-key="' + entityKey + '"]');
                if (liveContainer) {
                    liveContainer.innerHTML = ''
                        + '<div class="nm-signals-block__head">'
                        + '  <div class="nm-section-label">Sygnały</div>'
                        + '  <button class="nm-signals-block__cta" type="button" data-signal-open-submit>' + config.esc(config.submitButtonLabel || 'Dodaj uwagę do miejsca') + '</button>'
                        + '</div>'
                        + '<div class="ms-empty-state nm-empty-state"><strong>Nie udało się wczytać sygnałów</strong><span>' + config.esc(error && error.message ? error.message : 'Spróbuj ponownie za chwilę.') + '</span></div>';
                }

                clearFocus();
            })
            .finally(function() {
                if (activeRequest && activeRequest.controller === controller) {
                    activeRequest = null;
                }
            });
    }

    function updateSignalInCache(entityKey, signalId, updater) {
        var cachedData = entityCache[entityKey];
        if (!cachedData || !Array.isArray(cachedData.signals)) {
            return null;
        }

        for (var index = 0; index < cachedData.signals.length; index += 1) {
            var signal = cachedData.signals[index];
            if (String(signal.id) !== String(signalId)) {
                continue;
            }

            cachedData.signals[index] = updater(signal);
            return cachedData.signals[index];
        }

        return null;
    }

    function syncVoteButtons(card, value, signal) {
        if (!card) {
            return;
        }

        var upButton = card.querySelector('[data-signal-vote="1"]');
        var downButton = card.querySelector('[data-signal-vote="-1"]');
        var upValueEl = card.querySelector('[data-signal-score-up]');
        var downValueEl = card.querySelector('[data-signal-score-down]');

        if (upButton) {
            upButton.classList.toggle('is-active', value === 1);
            upButton.disabled = false;
        }
        if (downButton) {
            downButton.classList.toggle('is-active', value === -1);
            downButton.disabled = false;
        }
        if (upValueEl) {
            upValueEl.textContent = String(signal.score_up || 0);
        }
        if (downValueEl) {
            downValueEl.textContent = String(signal.score_down || 0);
        }
    }

    function applyVoteDelta(signal, previousVote, nextVote) {
        var updatedSignal = Object.assign({}, signal, {
            score_up: Number(signal.score_up || 0),
            score_down: Number(signal.score_down || 0),
        });

        if (previousVote === 1) {
            updatedSignal.score_up -= 1;
        } else if (previousVote === -1) {
            updatedSignal.score_down -= 1;
        }

        if (nextVote === 1) {
            updatedSignal.score_up += 1;
        } else if (nextVote === -1) {
            updatedSignal.score_down += 1;
        }

        return updatedSignal;
    }

    function handleVote(button) {
        var signalId = button.getAttribute('data-signal-id');
        var voteValue = normalizeVoteValue(button.getAttribute('data-signal-vote'));
        var card = button.closest('[data-signal-card]');
        var container = button.closest('[data-signal-entity-key]');
        var entityKey = container ? container.getAttribute('data-signal-entity-key') : null;
        if (!signalId || !card || !entityKey) {
            return;
        }

        var currentSignal = getSignalFromCache(entityKey, signalId);
        if (!currentSignal) {
            return;
        }

        var previousVote = getStoredVote(signalId);
        var nextVote = previousVote === voteValue ? 0 : voteValue;
        var optimisticSignal = applyVoteDelta(currentSignal, previousVote, nextVote);

        if (card.querySelectorAll('.nm-signal-vote-btn:disabled').length) {
            return;
        }

        card.querySelectorAll('.nm-signal-vote-btn').forEach(function(voteButton) {
            voteButton.disabled = true;
        });

        updateSignalInCache(entityKey, signalId, function() {
            return optimisticSignal;
        });
        setStoredVote(signalId, nextVote);
        syncVoteButtons(card, nextVote, optimisticSignal);

        fetch(config.baseUrl + 'api/signals/vote', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
            },
            body: 'signal_id=' + encodeURIComponent(signalId) + '&value=' + encodeURIComponent(String(nextVote)),
        })
            .then(function(response) {
                if (response.ok) {
                    return response.json();
                }

                return response.json()
                    .catch(function() { return null; })
                    .then(function(payload) {
                        throw new Error(payload && payload.message ? payload.message : 'Głos nie został zapisany.');
                    });
            })
            .then(function(payload) {
                var persistedVote = normalizeVoteValue(payload && payload.value != null ? payload.value : nextVote);
                var persistedSignal = updateSignalInCache(entityKey, signalId, function(signal) {
                    return Object.assign({}, signal, {
                        score_up: payload && payload.score_up != null ? Number(payload.score_up) : Number(signal.score_up || 0),
                        score_down: payload && payload.score_down != null ? Number(payload.score_down) : Number(signal.score_down || 0),
                    });
                });

                setStoredVote(signalId, persistedVote);
                syncVoteButtons(card, persistedVote, persistedSignal || optimisticSignal);
            })
            .catch(function() {
                updateSignalInCache(entityKey, signalId, function() {
                    return currentSignal;
                });
                setStoredVote(signalId, previousVote);
                syncVoteButtons(card, previousVote, currentSignal);
            });
    }

    function getCurrentContextFromContainer(container) {
        return {
            entityType: container.getAttribute('data-signal-entity-type') || '',
            entityId: container.getAttribute('data-signal-entity-id') || '',
        };
    }

    function bindEvents() {
        ensureLightbox();

        config.detailsContentEl.addEventListener('click', function(event) {
            var submitButton = event.target.closest('[data-signal-open-submit]');
            if (submitButton) {
                var submitContainer = submitButton.closest('[data-signal-entity-key]');
                if (config.onOpenSubmitForm && submitContainer) {
                    config.onOpenSubmitForm(getCurrentContextFromContainer(submitContainer));
                }
                return;
            }

            var attachmentButton = event.target.closest('[data-signal-attachment]');
            if (attachmentButton) {
                var attachmentCard = attachmentButton.closest('[data-signal-card]');
                var attachmentContainer = attachmentButton.closest('[data-signal-entity-key]');
                var attachmentEntityKey = attachmentContainer ? attachmentContainer.getAttribute('data-signal-entity-key') : null;
                var attachmentSignalId = attachmentCard ? attachmentCard.getAttribute('data-signal-id') : null;
                var attachmentSignal = attachmentEntityKey && attachmentSignalId
                    ? getSignalFromCache(attachmentEntityKey, attachmentSignalId)
                    : null;
                var attachmentIndex = parseInt(attachmentButton.getAttribute('data-signal-attachment-index'), 10);
                // The thumbnails render the filtered list, so the lightbox has to
                // walk the same one or the index would point at the wrong photo.
                openLightbox(visibleAttachments(attachmentSignal), isNaN(attachmentIndex) ? 0 : attachmentIndex);
                return;
            }

            var voteButton = event.target.closest('[data-signal-vote]');
            if (voteButton) {
                handleVote(voteButton);
                return;
            }

            // Footer controls (report, favourite) have their own handlers —
            // clicking them must not collapse/expand the signal card.
            if (event.target.closest('[data-signal-report]') || event.target.closest('[data-rcn-fav-toggle]')) {
                return;
            }

            var signalCard = event.target.closest('[data-signal-card]');
            if (signalCard) {
                var signalContainer = signalCard.closest('[data-signal-entity-key]');
                var signalEntityKey = signalContainer ? signalContainer.getAttribute('data-signal-entity-key') : null;
                var signalId = signalCard.getAttribute('data-signal-id');
                if (signalEntityKey && signalId) {
                    if (!signalCard.classList.contains('is-active')) {
                        focusSignal(signalEntityKey, signalId);
                    } else if (event.target.closest('[data-signal-toggle-label]')) {
                        // Rozwinięty sygnał zwija wyłącznie „Zwiń szczegóły”.
                        // Klik w treść, zdjęcia czy zaznaczanie tekstu w karcie
                        // nie może go zamykać.
                        unfocusSignal(signalEntityKey, signalId);
                    }
                }
            }
        });
    }
    bindEvents();

    return {
        buildSectionShell: buildSectionShell,
        clearFocus: clearFocus,
        fetchEntitySignals: fetchEntitySignals,
        setPendingFocus: setPendingFocus,
    };
}
